<div class="shipping-selector-compact">
  <!-- Confirmation Dialog -->
  <p-confirmDialog />

  <!-- Site Required Message -->
  @if (!siteId) {
    <div class="info-message">
      <i class="pi pi-info-circle"></i>
      <span>Site ID is required to load shipping addresses</span>
    </div>
  }

  @if (siteId) {
    <!-- Accordion Wrapper for Shipping Address Options -->
    <p-accordion [activeIndex]="0" styleClass="compact-accordion">
      <p-accordionpanel value="0">
        <p-accordionheader>
          <div class="accordion-header-content">
            <i class="pi pi-map-marker"></i>
            <span>Shipping Address</span>
          </div>
        </p-accordionheader>
        <p-accordioncontent>
          <div class="accordion-section-content">
            <!-- Address Source Toggle (Radio Buttons) -->
            <div class="address-source-toggle">
              <div
                class="toggle-option"
                [class.selected]="addressSource === 'customer'"
                (click)="setAddressSource('customer')"
              >
                <input
                  type="radio"
                  id="source-customer"
                  name="addressSource"
                  value="customer"
                  [(ngModel)]="addressSource"
                  [disabled]="disabled"
                />
                <label for="source-customer" class="toggle-label">
                  <i class="pi pi-user"></i>
                  <span>Use Customer Address</span>
                </label>
              </div>

              <div
                class="toggle-option"
                [class.selected]="addressSource === 'manual'"
                (click)="setAddressSource('manual')"
              >
                <input
                  type="radio"
                  id="source-manual"
                  name="addressSource"
                  value="manual"
                  [(ngModel)]="addressSource"
                  [disabled]="disabled"
                />
                <label for="source-manual" class="toggle-label">
                  <i class="pi pi-pencil"></i>
                  <span>Enter Manually</span>
                </label>
              </div>
            </div>

            <!-- Customer Address Mode -->
            @if (addressSource === "customer") {
              <div class="address-mode-content">
                <!-- Loading State -->
                @if (isLoading) {
                  <div class="loading-inline">
                    <p-skeleton
                      width="100%"
                      height="2.5rem"
                      borderRadius="0.5rem"
                    />
                    <p-skeleton
                      width="100%"
                      height="4rem"
                      borderRadius="0.5rem"
                      styleClass="mt-3"
                    />
                  </div>
                }

                <!-- Address Selector -->
                @if (!isLoading) {
                  <div class="field-compact">
                    <label for="shipping-address" class="field-label">
                      Select Shipping Address
                      @if (required) {
                        <span class="required">*</span>
                      }
                    </label>
                    <p-select
                      inputId="shipping-address"
                      [formControl]="formGroup.controls.siteShippingAddressId"
                      [options]="shippingAddressOptions"
                      (onChange)="onShippingAddressChange($event)"
                      [disabled]="disabled || isLoading"
                      placeholder="Select a shipping address"
                      styleClass="compact-select"
                      [showClear]="!required"
                      [filter]="true"
                      filterPlaceholder="Search addresses..."
                      [filterBy]="'label'"
                      appendTo="body"
                    >
                      <ng-template let-option pTemplate="item">
                        <div class="address-option">
                          <i class="pi pi-map-marker"></i>
                          <span>{{ option.label }}</span>
                        </div>
                      </ng-template>
                    </p-select>

                    @if (!isLoading && shippingAddresses.length === 0) {
                      <small class="help-text warning">
                        <i class="pi pi-exclamation-triangle"></i>
                        No shipping addresses found. Please add one in Settings.
                      </small>
                    }

                    @if (
                      required &&
                      !formGroup.controls.siteShippingAddressId.value &&
                      !isLoading
                    ) {
                      <small class="error-text">
                        Shipping address is required
                      </small>
                    }
                  </div>

                  <!-- Selected Address Preview -->
                  @if (hasShippingAddress() && !isLoading) {
                    <div class="address-preview">
                      <div class="preview-header">
                        <i class="pi pi-map-marker"></i>
                        <span>Shipping To</span>
                      </div>
                      <div class="preview-content">
                        {{ getFormattedAddress() }}
                      </div>
                    </div>
                  }
                }
              </div>
            }

            <!-- Manual Entry Mode -->
            @if (addressSource === "manual") {
              <div class="address-mode-content">
                <div class="manual-fields-grid">
                  <!-- First Name -->
                  <div class="field-compact">
                    <label for="shipping-first-name" class="field-label">
                      First Name
                      @if (required) {
                        <span class="required">*</span>
                      }
                    </label>
                    <input
                      pInputText
                      id="shipping-first-name"
                      [formControl]="formGroup.controls.shippingFirstName"
                      (input)="onManualFieldChange()"
                      [disabled]="disabled"
                      placeholder="First Name"
                      class="compact-input"
                    />
                  </div>

                  <!-- Last Name -->
                  <div class="field-compact">
                    <label for="shipping-last-name" class="field-label">
                      Last Name
                      @if (required) {
                        <span class="required">*</span>
                      }
                    </label>
                    <input
                      pInputText
                      id="shipping-last-name"
                      [formControl]="formGroup.controls.shippingLastName"
                      (input)="onManualFieldChange()"
                      [disabled]="disabled"
                      placeholder="Last Name"
                      class="compact-input"
                    />
                  </div>

                  <!-- Country -->
                  <div class="field-compact">
                    <label for="shipping-country" class="field-label">
                      Country
                      @if (required) {
                        <span class="required">*</span>
                      }
                    </label>
                    <p-select
                      inputId="shipping-country"
                      [formControl]="formGroup.controls.shippingCountry"
                      [options]="countryOptions"
                      (onChange)="onCountryChange()"
                      [disabled]="disabled"
                      placeholder="Select Country"
                      styleClass="compact-select"
                      [filter]="true"
                      filterPlaceholder="Search countries..."
                      appendTo="body"
                    />
                  </div>

                  <!-- Street 1 -->
                  <div class="field-compact">
                    <label for="shipping-street1" class="field-label">
                      Street Address
                      @if (required) {
                        <span class="required">*</span>
                      }
                    </label>
                    <input
                      pInputText
                      id="shipping-street1"
                      [formControl]="formGroup.controls.shippingStreet1"
                      (input)="onManualFieldChange()"
                      [disabled]="disabled"
                      placeholder="Street Address"
                      class="compact-input"
                    />
                  </div>

                  <!-- Street 2 -->
                  <div class="field-compact">
                    <label for="shipping-street2" class="field-label">
                      Street Address 2
                    </label>
                    <input
                      pInputText
                      id="shipping-street2"
                      [formControl]="formGroup.controls.shippingStreet2"
                      (input)="onManualFieldChange()"
                      [disabled]="disabled"
                      placeholder="Apt, Suite, etc. (Optional)"
                      class="compact-input"
                    />
                  </div>

                  <!-- City -->
                  <div class="field-compact">
                    <label for="shipping-city" class="field-label">
                      City
                      @if (required) {
                        <span class="required">*</span>
                      }
                    </label>
                    <input
                      pInputText
                      id="shipping-city"
                      [formControl]="formGroup.controls.shippingCity"
                      (input)="onManualFieldChange()"
                      [disabled]="disabled"
                      placeholder="City"
                      class="compact-input"
                    />
                  </div>

                  <!-- State - Dropdown for USA, Text input for other countries -->
                  <div class="field-compact">
                    <label for="shipping-state" class="field-label">
                      State/Province
                      @if (required) {
                        <span class="required">*</span>
                      }
                    </label>
                    @if (isUSACountry()) {
                      <p-select
                        inputId="shipping-state"
                        [formControl]="formGroup.controls.shippingState"
                        [options]="stateOptions"
                        (onChange)="onManualFieldChange()"
                        [disabled]="disabled"
                        placeholder="Select State"
                        styleClass="compact-select"
                        [filter]="true"
                        filterPlaceholder="Search states..."
                        appendTo="body"
                      />
                    } @else {
                      <input
                        pInputText
                        id="shipping-state"
                        [formControl]="formGroup.controls.shippingState"
                        (input)="onManualFieldChange()"
                        [disabled]="disabled"
                        placeholder="State/Province"
                        class="compact-input"
                      />
                    }
                  </div>

                  <!-- Postcode -->
                  <div class="field-compact">
                    <label for="shipping-postcode" class="field-label">
                      Postal Code
                      @if (required) {
                        <span class="required">*</span>
                      }
                    </label>
                    <input
                      pInputText
                      id="shipping-postcode"
                      [formControl]="formGroup.controls.shippingPostcode"
                      (input)="onManualFieldChange()"
                      [disabled]="disabled"
                      placeholder="Postal/Zip Code"
                      class="compact-input"
                    />
                  </div>
                </div>

                <!-- Validation Message -->
                @if (
                  required &&
                  addressSource === "manual" &&
                  (!formGroup.controls.shippingFirstName.value ||
                    !formGroup.controls.shippingLastName.value ||
                    !formGroup.controls.shippingStreet1.value ||
                    !formGroup.controls.shippingCity.value ||
                    !formGroup.controls.shippingState.value ||
                    !formGroup.controls.shippingPostcode.value ||
                    !formGroup.controls.shippingCountry.value)
                ) {
                  <small class="error-text">
                    <i class="pi pi-exclamation-triangle"></i>
                    Please fill all required shipping address fields
                  </small>
                }
              </div>
            }
          </div>
        </p-accordioncontent>
      </p-accordionpanel>
    </p-accordion>
  }
</div>
